import React, { useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import api from './api';
import { API_BASE_URL } from './api';

function isTournamentRequestMatch(match) {
  return Boolean(match?.isBye) || String(match?.notes || '').toLowerCase() === 'tournament live request match';
}

function getSeriesScore(match, myParticipantId) {
  const myId = String(myParticipantId || '');
  if (!myId) return { mine: null, opponent: null };
  if (String(match?.player1ParticipantId || '') === myId) {
    return {
      mine: Number(match?.score1 ?? 0),
      opponent: Number(match?.score2 ?? 0),
    };
  }
  if (String(match?.player2ParticipantId || '') === myId) {
    return {
      mine: Number(match?.score2 ?? 0),
      opponent: Number(match?.score1 ?? 0),
    };
  }
  return { mine: null, opponent: null };
}

function getRoundResultLabel(match, myParticipantId) {
  if (!match) return 'Awaiting';
  if (match.isBye) return 'Advanced';
  if (!match.winnerParticipantId) return 'Pending';
  return String(match.winnerParticipantId) === String(myParticipantId) ? 'Won' : 'Lost';
}

function getRoundProgress(match, myParticipantId, targetWins) {
  const score = getSeriesScore(match, myParticipantId);
  const target = Math.max(1, Number(targetWins || 1));
  const mine = Math.max(0, Number(score.mine || 0));
  const opponent = Math.max(0, Number(score.opponent || 0));
  const minePct = Math.min(100, Math.round((mine / target) * 100));
  const opponentPct = Math.min(100, Math.round((opponent / target) * 100));
  return {
    mine,
    opponent,
    target,
    minePct,
    opponentPct,
    scoreLine: `${mine} - ${opponent}`,
  };
}

function roundsNeeded(players) {
  let count = Math.max(1, Number(players || 0));
  let rounds = 0;
  while (count > 1) {
    count = Math.ceil(count / 2);
    rounds += 1;
  }
  return Math.max(rounds, 1);
}

function formatRankRange(startRank, endRank) {
  if (!startRank || !endRank) return '';
  return Number(startRank) === Number(endRank) ? `${startRank}` : `${startRank}-${endRank}`;
}

function formatDateTime(value) {
  if (!value) return 'Not played yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not played yet';
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function roundIncludesParticipant(round, participantId) {
  const myId = String(participantId || '');
  if (!myId || !round) return false;
  if ((round.participantIds || []).map(String).includes(myId)) return true;
  if ((round.byeParticipantIds || []).map(String).includes(myId)) return true;
  return (round.matches || []).some((match) =>
    String(match.player1ParticipantId || '') === myId || String(match.player2ParticipantId || '') === myId
  );
}

function isRoundResolvedForParticipant(round, participantId) {
  const myId = String(participantId || '');
  if (!myId || !round) return false;
  return (round.matches || []).some((match) =>
    String(match.winnerParticipantId || '') === myId || String(match.loserParticipantId || '') === myId
  ) || (round.byeParticipantIds || []).map(String).includes(myId);
}

export default function TournamentLivePage({ tournamentId, token, user, onBack, onMatchStart }) {
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [incomingTournamentReq, setIncomingTournamentReq] = useState(null);
  const [selectedRoundNumber, setSelectedRoundNumber] = useState(1);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window === 'undefined' ? 1280 : window.innerWidth));
  const socketRef = useRef(null);
  const hasManualRoundSelectionRef = useRef(false);
  const roundStepRefs = useRef({});

  async function loadTournamentState() {
    const res = await api.get(`/matches/tournaments/${tournamentId}`);
    setTournament(res.data?.data?.tournament || null);
  }

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/matches/tournaments/${tournamentId}`);
        if (!active) return;
        setTournament(res.data?.data?.tournament || null);
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.message || 'Failed to load tournament live page');
      } finally {
        if (active) setLoading(false);
      }
    }
    if (tournamentId) load();
    return () => {
      active = false;
    };
  }, [tournamentId]);

  useEffect(() => {
    if (!tournamentId) return undefined;
    const timer = setInterval(() => {
      loadTournamentState().catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [tournamentId]);

  useEffect(() => {
    if (!token) return undefined;
    const socket = io(API_BASE_URL, {
      auth: { token },
      transports: ['websocket'],
    });
    socketRef.current = socket;

    socket.on('online:users', (users) => {
      setOnlineUsers(Array.isArray(users) ? users : []);
    });
    socket.on('tournament:request-sent', () => {
      setSendingRequest(true);
      setNotice('Tournament request sent.');
    });
    socket.on('tournament:request-received', ({ requestId, tournamentId: nextTournamentId, tournamentName, roundNumber, bracketType, stageKey, roundLabel, from }) => {
      setIncomingTournamentReq({ requestId, tournamentId: nextTournamentId, tournamentName, roundNumber, bracketType, stageKey, roundLabel, from });
      setNotice(`Tournament request received for ${roundLabel || tournamentName}.`);
    });
    socket.on('tournament:request-response', ({ fromUser, accepted }) => {
      setSendingRequest(false);
      setNotice(accepted ? `${fromUser.name} accepted your tournament request.` : `${fromUser.name} rejected your tournament request.`);
    });
    socket.on('tournament:request-closed', () => {
      setSendingRequest(false);
      setIncomingTournamentReq(null);
      setNotice('Another player accepted that tournament request first.');
    });
    socket.on('online:request-error', ({ message }) => {
      setSendingRequest(false);
      setNotice(message || 'Tournament request failed');
    });
    socket.on('online:match-start', ({ roomId, opponent, color, state, stake, session, tournament: tournamentMeta }) => {
      if (typeof onMatchStart === 'function') {
        onMatchStart({ roomId, opponent, color, state, stake, session, tournament: tournamentMeta });
      }
    });
    socket.on('tournament:update', ({ tournamentId: updatedTournamentId }) => {
      if (String(updatedTournamentId || '') !== String(tournamentId || '')) return;
      loadTournamentState().catch(() => {});
    });

    return () => {
      socket.disconnect();
    };
  }, [onMatchStart, token, tournamentId]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    function handleResize() {
      setViewportWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    hasManualRoundSelectionRef.current = false;
    setSelectedRoundNumber(1);
  }, [tournamentId]);

  useEffect(() => {
    const activeStep = roundStepRefs.current[Number(selectedRoundNumber || 0)];
    if (!activeStep) return;
    activeStep.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [selectedRoundNumber, tournamentId]);

  const myEntryStatus = String(tournament?.myEntry?.status || '').toLowerCase();
  const isNarrowScreen = viewportWidth < 560;
  const isEliminated = ['eliminated', 'ranked'].includes(myEntryStatus);
  const myParticipantId = String(tournament?.myEntry?.id || '');
  const mainRounds = useMemo(
    () => [...(tournament?.rounds || [])]
      .filter((round) => String(round.bracketType || '') === 'main')
      .sort((a, b) => Number(a.roundNumber || 0) - Number(b.roundNumber || 0)),
    [tournament]
  );
  const placementRounds = useMemo(
    () => [...(tournament?.rounds || [])]
      .filter((round) => String(round.bracketType || '') !== 'main')
      .sort((a, b) => Number(a.stageOrder || 0) - Number(b.stageOrder || 0)),
    [tournament]
  );
  const activeRound = useMemo(() => {
    return mainRounds.find((round) => Number(round.roundNumber || 0) === Number(tournament?.activeRoundNumber || 0)) || null;
  }, [mainRounds, tournament]);

  const currentPlayableRound = useMemo(() => {
    const rounds = tournament?.rounds || [];
    const ordered = [...rounds].sort((a, b) => {
      const statusA = String(a.status || '').toLowerCase();
      const statusB = String(b.status || '').toLowerCase();
      if (statusA === 'live' && statusB !== 'live') return -1;
      if (statusB === 'live' && statusA !== 'live') return 1;
      if (String(a.bracketType || '') === 'main' && String(b.bracketType || '') !== 'main') return -1;
      if (String(b.bracketType || '') === 'main' && String(a.bracketType || '') !== 'main') return 1;
      return Number(a.stageOrder || 0) - Number(b.stageOrder || 0);
    });

    return ordered.find((round) =>
      roundIncludesParticipant(round, myParticipantId)
      && ['live', 'upcoming', 'active'].includes(String(round.status || '').toLowerCase())
      && !isRoundResolvedForParticipant(round, myParticipantId)
    ) || null;
  }, [myParticipantId, tournament]);

  const placementPlayableRound = useMemo(() => {
    const rounds = tournament?.rounds || [];
    return rounds.find((round) =>
      String(round.bracketType || '') !== 'main'
      && roundIncludesParticipant(round, myParticipantId)
      && !isRoundResolvedForParticipant(round, myParticipantId)
    ) || null;
  }, [myParticipantId, tournament]);

  useEffect(() => {
    if (!tournament) return;
    const nextRound = Number(tournament.activeRoundNumber || 1) || 1;
    setSelectedRoundNumber((current) => (current ? current : nextRound));
  }, [tournament]);

  const myMatch = useMemo(() => {
    const myId = String(tournament?.myEntry?.id || '');
    if (!myId || !activeRound) return null;
    return (activeRound.matches || []).find((match) =>
      isTournamentRequestMatch(match) && (
      String(match.player1ParticipantId || '') === myId || String(match.player2ParticipantId || '') === myId
      )
    ) || null;
  }, [activeRound, tournament]);

  const effectivePlayableRound = placementPlayableRound || currentPlayableRound || null;

  const currentPlayableRoundMatch = useMemo(() => {
    if (!myParticipantId || !effectivePlayableRound) return null;
    return (effectivePlayableRound.matches || []).find((match) =>
      isTournamentRequestMatch(match) && (
        String(match.player1ParticipantId || '') === myParticipantId || String(match.player2ParticipantId || '') === myParticipantId
      )
    ) || null;
  }, [effectivePlayableRound, myParticipantId]);

  const totalRounds = useMemo(() => roundsNeeded(tournament?.maxPlayers || 50), [tournament]);
  const roundsList = useMemo(() => Array.from({ length: totalRounds }, (_, index) => index + 1), [totalRounds]);

  const myMatchesByRound = useMemo(() => {
    const map = new Map();
    mainRounds.forEach((round) => {
      const match = (round.matches || []).find((entry) =>
        String(entry.player1ParticipantId || '') === myParticipantId || String(entry.player2ParticipantId || '') === myParticipantId
      );
      const resolvedMatch = match && (
        match.isBye
        || match.winnerParticipantId
        || String(match.status || '').toLowerCase() === 'completed'
      )
        ? match
        : null;
      if (resolvedMatch) map.set(Number(round.roundNumber || 0), { ...resolvedMatch, roundStatus: round.status, label: round.label });
      else if ((round.byeParticipantIds || []).includes(myParticipantId)) {
        map.set(Number(round.roundNumber || 0), {
          id: `bye-${round.roundNumber}`,
          isBye: true,
          winnerParticipantId: myParticipantId,
          player1Name: tournament?.myEntry?.name || user?.name || 'You',
          player2Name: 'BYE',
          score1: Number(tournament?.matchTargetWins || 5),
          score2: 0,
          completedAt: round.completedAt || null,
          roundStatus: round.status,
          label: round.label,
        });
      }
    });
    return map;
  }, [mainRounds, myParticipantId, tournament, user]);

  const playerFocusRoundNumber = useMemo(() => {
    const qualifyingRound = mainRounds.find((round) => roundIncludesParticipant(round, myParticipantId) && ['upcoming', 'live', 'active'].includes(String(round.status || '').toLowerCase()));
    if (qualifyingRound) return Number(qualifyingRound.roundNumber || 1);

    let latestQualifiedRound = 0;
    let latestPlayedRound = 0;
    mainRounds.forEach((round) => {
      const roundNumber = Number(round.roundNumber || 0);
      if (!roundNumber) return;
      if (roundIncludesParticipant(round, myParticipantId)) {
        latestPlayedRound = Math.max(latestPlayedRound, roundNumber);
      }
      const myRoundMatch = (round.matches || []).find((match) =>
        String(match.player1ParticipantId || '') === myParticipantId || String(match.player2ParticipantId || '') === myParticipantId
      );
      const advancedByBye = (round.byeParticipantIds || []).map(String).includes(myParticipantId);
      const wonRound = Boolean(myRoundMatch?.winnerParticipantId) && String(myRoundMatch.winnerParticipantId) === myParticipantId;
      if (advancedByBye || wonRound) latestQualifiedRound = Math.max(latestQualifiedRound, roundNumber + 1);
    });

    if (latestQualifiedRound > 0) return Math.min(totalRounds, latestQualifiedRound);
    if (latestPlayedRound > 0) return latestPlayedRound;
    return Math.max(1, Number(tournament?.activeRoundNumber || 1));
  }, [mainRounds, myParticipantId, totalRounds, tournament]);
  const lastParticipatedRoundNumber = useMemo(() => {
    let latestRound = 0;
    mainRounds.forEach((round) => {
      const roundNumber = Number(round.roundNumber || 0);
      if (!roundNumber) return;
      if (roundIncludesParticipant(round, myParticipantId)) {
        latestRound = Math.max(latestRound, roundNumber);
      }
    });
    return latestRound || 1;
  }, [mainRounds, myParticipantId]);
  const latestQualifiedRoundNumber = useMemo(() => {
    let latestQualifiedRound = 0;
    mainRounds.forEach((round) => {
      const roundNumber = Number(round.roundNumber || 0);
      if (!roundNumber) return;
      const myRoundMatch = (round.matches || []).find((match) =>
        String(match.player1ParticipantId || '') === myParticipantId || String(match.player2ParticipantId || '') === myParticipantId
      );
      const advancedByBye = (round.byeParticipantIds || []).map(String).includes(myParticipantId);
      const wonRound = Boolean(myRoundMatch?.winnerParticipantId) && String(myRoundMatch.winnerParticipantId) === myParticipantId;
      if (advancedByBye || wonRound) latestQualifiedRound = Math.max(latestQualifiedRound, roundNumber + 1);
    });
    return latestQualifiedRound > 0 ? Math.min(totalRounds, latestQualifiedRound) : 0;
  }, [mainRounds, myParticipantId, totalRounds]);

  const selectedRoundData = useMemo(() => {
    const existing = mainRounds.find((round) => Number(round.roundNumber || 0) === Number(selectedRoundNumber || 0));
    if (existing) return existing;
    const isVirtuallyQualified = Number(selectedRoundNumber || 0) <= Number(latestQualifiedRoundNumber || 0);
    return {
      bracketType: 'main',
      roundNumber: selectedRoundNumber,
      label: `Round ${selectedRoundNumber}`,
      status: Number(selectedRoundNumber || 0) <= Number(tournament?.activeRoundNumber || 0) ? 'live' : 'upcoming',
      participantIds: isVirtuallyQualified ? [myParticipantId] : [],
      matches: [],
      byeParticipantIds: [],
    };
  }, [latestQualifiedRoundNumber, mainRounds, myParticipantId, selectedRoundNumber, tournament]);

  const selectedRoundMatch = myMatchesByRound.get(Number(selectedRoundNumber || 0)) || null;
  const selectedRoundOpponent = selectedRoundMatch
    ? (String(selectedRoundMatch.player1ParticipantId || '') === myParticipantId
      ? (selectedRoundMatch.player2Name || 'BYE')
      : (selectedRoundMatch.player1Name || 'TBD'))
    : '';
  const selectedRoundScore = selectedRoundMatch ? getSeriesScore(selectedRoundMatch, myParticipantId) : { mine: null, opponent: null };
  const selectedRoundProgress = selectedRoundMatch ? getRoundProgress(selectedRoundMatch, myParticipantId, tournament?.matchTargetWins) : null;
  const selectedRoundResult = getRoundResultLabel(selectedRoundMatch, myParticipantId);
  const selectedRoundResolved = Boolean(
    selectedRoundMatch && (
      selectedRoundMatch.isBye
      || selectedRoundMatch.winnerParticipantId
      || String(selectedRoundMatch.status || '').toLowerCase() === 'completed'
    )
  );
  const selectedRoundLost = selectedRoundResolved
    && !selectedRoundMatch?.isBye
    && Boolean(selectedRoundMatch?.winnerParticipantId)
    && String(selectedRoundMatch.winnerParticipantId) !== myParticipantId;
  const selectedRoundIsQualified = roundIncludesParticipant(selectedRoundData, myParticipantId);
  const isWaitingForRoundStart = selectedRoundIsQualified
    && !selectedRoundMatch
    && ['upcoming', 'pending'].includes(String(selectedRoundData?.status || '').toLowerCase());
  const lastCompletedRoundNumber = useMemo(() => {
    const completedRounds = mainRounds
      .filter((round) => String(round.status || '').toLowerCase() === 'completed')
      .map((round) => Number(round.roundNumber || 0))
      .filter(Boolean);
    return completedRounds.length ? Math.max(...completedRounds) : 0;
  }, [mainRounds]);
  const nextRoundToStartNumber = useMemo(() => {
    const upcoming = mainRounds
      .filter((round) => ['upcoming', 'pending'].includes(String(round.status || '').toLowerCase()))
      .map((round) => Number(round.roundNumber || 0))
      .filter(Boolean)
      .sort((a, b) => a - b)[0];
    if (upcoming) return upcoming;
    if (lastCompletedRoundNumber > 0 && lastCompletedRoundNumber < totalRounds) return lastCompletedRoundNumber + 1;
    return 0;
  }, [lastCompletedRoundNumber, mainRounds, totalRounds]);
  const activeRoundNumber = Number(tournament?.activeRoundNumber || 0);
  const isSubMatchRound = String(effectivePlayableRound?.bracketType || '') !== 'main';
  const canPlayCurrentRound = Boolean(effectivePlayableRound) && !isRoundResolvedForParticipant(effectivePlayableRound, myParticipantId);
  const shouldShowRoundNotStartedButton = !isSubMatchRound
    && selectedRoundIsQualified
    && Number(selectedRoundNumber || 0) > activeRoundNumber
    && Number(effectivePlayableRound?.roundNumber || 0) === Number(selectedRoundNumber || 0);
  const inferredLastCompletedRoundNumber = Number(selectedRoundNumber || 0) > activeRoundNumber
    ? activeRoundNumber
    : lastCompletedRoundNumber;
  const inferredNextRoundToStartNumber = Number(selectedRoundNumber || 0) > activeRoundNumber
    ? Number(selectedRoundNumber || 0)
    : nextRoundToStartNumber;
  const selectedRoundWon = Boolean(selectedRoundMatch?.winnerParticipantId) && String(selectedRoundMatch.winnerParticipantId) === myParticipantId;
  const isFinalMainRound = totalRounds > 0 && Number(selectedRoundNumber || 0) >= totalRounds;
  const nextQualifiedRoundNumber = selectedRoundWon || selectedRoundMatch?.isBye
    ? (Number(selectedRoundNumber || 0) < totalRounds ? Number(selectedRoundNumber || 0) + 1 : 0)
    : 0;
  const shouldShowNextRoundWaitingInfo = isWaitingForRoundStart
    || Number(selectedRoundNumber || 0) > activeRoundNumber
    || ((selectedRoundWon || selectedRoundMatch?.isBye)
      && nextRoundToStartNumber > Number(selectedRoundNumber || 0));
  const highestOpenRound = Math.min(
    totalRounds,
    Math.max(
      1,
      Number(latestQualifiedRoundNumber || 0) > 0
        ? Number(latestQualifiedRoundNumber || 1)
        : Number(lastParticipatedRoundNumber || playerFocusRoundNumber || 1)
    )
  );
  const currentRoundMatchCount = Number(activeRound?.matches?.filter((match) => !match.isBye && match.status === 'completed').length || 0);
  const selectedRoundStarted = Boolean(mainRounds.some((round) => Number(round.roundNumber || 0) === Number(selectedRoundNumber || 0)));
  const shouldShowLiveRoundCard = !selectedRoundMatch
    && Number(selectedRoundNumber || 0) === Number(activeRoundNumber || 0)
    && selectedRoundIsQualified
    && !isEliminated;
  const eligibleOnlineRoundPlayers = useMemo(() => {
    const targetRound = effectivePlayableRound || selectedRoundData;
    if (!targetRound) return [];
    const targetIsMainActive = String(targetRound.bracketType || '') === 'main'
      && Number(targetRound.roundNumber || 0) === Number(activeRoundNumber || 0);
    const targetIsPlacementLive = String(targetRound.bracketType || '') !== 'main'
      && String(targetRound.status || '').toLowerCase() === 'live';
    if (!targetIsMainActive && !targetIsPlacementLive) return [];
    const resolvedParticipantIds = new Set(
      (targetRound.matches || [])
        .flatMap((match) => [match.winnerParticipantId, match.loserParticipantId])
        .filter(Boolean)
        .map(String)
    );
    const roundParticipantIds = new Set((targetRound.participantIds || []).map(String));
    return onlineUsers.filter((onlineUser) => {
      const participant = (tournament?.participants || []).find((entry) => String(entry.userId) === String(onlineUser.id || ''));
      if (!participant) return false;
      const participantId = String(participant.id || '');
      if (!roundParticipantIds.has(participantId)) return false;
      if (resolvedParticipantIds.has(participantId)) return false;
      return String(onlineUser.id || '') !== String(user?.id || '');
    });
  }, [activeRoundNumber, effectivePlayableRound, onlineUsers, selectedRoundData, tournament, user?.id]);

  const latestPlacementMatch = useMemo(() => {
    const placementMatchRows = placementRounds.flatMap((round) =>
      (round.matches || [])
        .filter((match) =>
          String(match.player1ParticipantId || '') === myParticipantId || String(match.player2ParticipantId || '') === myParticipantId
        )
        .map((match) => ({ ...match, roundLabel: round.label, roundStatus: round.status }))
    );
    return placementMatchRows.sort((a, b) => new Date(b.completedAt || b.createdAt || 0).getTime() - new Date(a.completedAt || a.createdAt || 0).getTime())[0] || null;
  }, [myParticipantId, placementRounds]);
  const latestPlacementOpponent = latestPlacementMatch
    ? (String(latestPlacementMatch.player1ParticipantId || '') === myParticipantId
      ? (latestPlacementMatch.player2Name || 'BYE')
      : (latestPlacementMatch.player1Name || 'TBD'))
    : '';
  const latestPlacementProgress = latestPlacementMatch ? getRoundProgress(latestPlacementMatch, myParticipantId, tournament?.matchTargetWins) : null;
  const latestPlacementResult = getRoundResultLabel(latestPlacementMatch, myParticipantId);
  const hasResolvedPlacementMatch = Boolean(
    latestPlacementMatch && (
      latestPlacementMatch.winnerParticipantId
      || latestPlacementMatch.isBye
      || String(latestPlacementMatch.status || latestPlacementMatch.roundStatus || '').toLowerCase() === 'completed'
    )
  );
  const placementRoundsForSelectedRound = useMemo(() => placementRounds.filter((round) => {
    const sourceMainRoundNumber = Number(round.sourceMainRoundNumber || 0);
    if (sourceMainRoundNumber > 0) {
      return sourceMainRoundNumber === Number(selectedRoundNumber || 0);
    }
    const bracketType = String(round.bracketType || '');
    if (Number(selectedRoundNumber || 0) === 3) return bracketType === 'placement-8-9';
    if (Number(selectedRoundNumber || 0) === 4) return bracketType === 'placement-5-7';
    if (Number(selectedRoundNumber || 0) === 5) return bracketType === 'placement-3-4';
    return false;
  }), [placementRounds, selectedRoundNumber]);
  const selectedPlacementContext = placementRoundsForSelectedRound[0] || null;
  const shouldShowPlacementInstruction = placementRoundsForSelectedRound.length > 0 && !hasResolvedPlacementMatch;
  const placementInstructionRange = formatRankRange(
    Number(selectedPlacementContext?.placementStartRank || 0),
    Number(selectedPlacementContext?.placementEndRank || 0)
  );
  const mainRoundHistory = useMemo(() => {
    return mainRounds
      .map((round) => {
        const match = myMatchesByRound.get(Number(round.roundNumber || 0));
        if (!match) return null;
        const isPlayerOne = String(match.player1ParticipantId || '') === String(myParticipantId || '');
        return {
          id: `main-${round.roundNumber}-${match.id || 'bye'}`,
          label: round.label || `Round ${round.roundNumber}`,
          roundNumber: Number(round.roundNumber || 0),
          opponent: match.isBye
            ? 'BYE'
            : (isPlayerOne ? (match.player2Name || 'TBD') : (match.player1Name || 'TBD')),
          scoreLine: match.isBye
            ? `${tournament?.matchTargetWins || 5}-0`
            : `${isPlayerOne ? match.score1 : match.score2}-${isPlayerOne ? match.score2 : match.score1}`,
          result: getRoundResultLabel(match, myParticipantId),
          playedAt: formatDateTime(match.completedAt || match.createdAt),
        };
      })
      .filter(Boolean);
  }, [mainRounds, myMatchesByRound, myParticipantId, tournament]);
  const placementHistory = useMemo(() => placementRounds.flatMap((round) =>
    (round.matches || [])
      .filter((match) =>
        String(match.player1ParticipantId || '') === myParticipantId || String(match.player2ParticipantId || '') === myParticipantId
      )
      .map((match) => {
        const isPlayerOne = String(match.player1ParticipantId || '') === myParticipantId;
        return {
          id: `${round.stageKey}-${match.id || match.slot}`,
          label: round.label,
          status: match.status || round.status,
          opponent: isPlayerOne ? (match.player2Name || 'BYE') : (match.player1Name || 'TBD'),
          scoreLine: `${isPlayerOne ? match.score1 : match.score2}-${isPlayerOne ? match.score2 : match.score1}`,
          result: getRoundResultLabel(match, myParticipantId),
          playedAt: formatDateTime(match.completedAt || match.createdAt),
        };
      })
  ), [myParticipantId, placementRounds]);

  useEffect(() => {
    if (!tournament) return;
    if (hasManualRoundSelectionRef.current) return;
    setSelectedRoundNumber(Number(playerFocusRoundNumber || 1));
  }, [playerFocusRoundNumber, tournament]);

  function handleSendTournamentRequest() {
    if (!socketRef.current) return;
    const playableRound = effectivePlayableRound || activeRound;
    if (!playableRound) return;
    socketRef.current.emit('tournament:send-request', {
      tournamentId,
      roundNumber: Number(playableRound?.roundNumber || 0),
      bracketType: String(playableRound?.bracketType || 'main'),
      stageKey: String(playableRound?.stageKey || ''),
      roundLabel: String(playableRound?.label || ''),
    });
  }

  function handleTournamentRequestResponse(accepted) {
    if (!incomingTournamentReq || !socketRef.current) return;
    socketRef.current.emit('tournament:respond-request', {
      requestId: incomingTournamentReq.requestId,
      accepted,
    });
    setIncomingTournamentReq(null);
  }

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.header}>
          <div>
            <div style={styles.kicker}>Tournament Live</div>
            <div style={styles.title}>{tournament?.name || 'Tournament Lobby'}</div>
            {/* <div style={styles.meta}>Current round view for joined players.</div> */}
          </div>
          <button type="button" onClick={onBack} style={styles.backBtn}>Back</button>
        </div>

        {loading && <div style={styles.card}>Loading tournament...</div>}
        {!loading && error && <div style={{ ...styles.card, color: '#b14b3d' }}>{error}</div>}
        {notice && <div style={styles.notice}>{notice}</div>}

        {!loading && tournament && (
          <div style={styles.stack}>
            <div style={styles.summaryBar}>
              <span style={styles.summaryItem}><span style={styles.summaryKey}>Tournament</span><span style={styles.summaryText}>{tournament.name}</span></span>
              <span style={styles.summaryItem}><span style={styles.summaryKey}>Players</span><span style={styles.summaryText}>{tournament.participantCount}/{tournament.maxPlayers}</span></span>
              <span style={styles.summaryItem}><span style={styles.summaryKey}>Format</span><span style={styles.summaryText}>{tournament.matchTypeLabel}</span></span>
              <span style={styles.summaryItem}><span style={styles.summaryKey}>Current Round</span><span style={styles.summaryText}>Round {tournament.activeRoundNumber || '-'}</span></span>
              <span style={styles.summaryItem}><span style={styles.summaryKey}>Entry Fee</span><span style={styles.summaryText}>{tournament.entryFee}</span></span>
              <span style={styles.summaryItem}><span style={styles.summaryKey}>Round Progress</span><span style={styles.summaryText}>{currentRoundMatchCount} match done</span></span>
            </div>

            <div style={styles.timelineCard}>
              <div style={styles.cardTitle}>Round Progress</div>
              <div style={styles.timelineWrap}>
                {roundsList.map((roundNumber, index) => {
                  const roundMatch = myMatchesByRound.get(roundNumber);
                  const isSelected = Number(selectedRoundNumber || 0) === roundNumber;
                  const clickable = roundNumber <= highestOpenRound;
                  const isComplete = Boolean(roundMatch?.winnerParticipantId || roundMatch?.isBye);
                  const isCurrent = Number(playerFocusRoundNumber || 0) === roundNumber;
                  return (
                    <React.Fragment key={roundNumber}>
                      <button
                        type="button"
                        ref={(node) => {
                          if (node) {
                            roundStepRefs.current[roundNumber] = node;
                          } else {
                            delete roundStepRefs.current[roundNumber];
                          }
                        }}
                        disabled={!clickable}
                        onClick={() => {
                          if (!clickable) return;
                          hasManualRoundSelectionRef.current = true;
                          setSelectedRoundNumber(roundNumber);
                        }}
                        style={{
                          ...styles.roundDot,
                          ...(isComplete ? styles.roundDotDone : {}),
                          ...(isCurrent ? styles.roundDotCurrent : {}),
                          ...(isSelected ? styles.roundDotSelected : {}),
                          ...(!clickable ? styles.roundDotLocked : {}),
                        }}
                      >
                        <span style={styles.roundDotNumber}>{roundNumber}</span>
                        <span style={styles.roundDotLabel}>Round {roundNumber}</span>
                      </button>
                      {index < roundsList.length - 1 && <div style={styles.timelineLine} />}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            <div style={styles.card}>
              <div style={styles.cardTitle}>Round {selectedRoundNumber} Summary</div>
              <div style={styles.cardMeta}>Round label: {selectedRoundData?.label || `Round ${selectedRoundNumber}`}</div>
              {!shouldShowNextRoundWaitingInfo && (
                <div style={styles.cardMeta}>Round status: {selectedRoundData?.status || 'upcoming'}</div>
              )}

              {shouldShowNextRoundWaitingInfo && (
                <>
                  {inferredLastCompletedRoundNumber > 0 && (
                    <div style={styles.cardMeta}>Last completed round: Round {inferredLastCompletedRoundNumber}</div>
                  )}
                  {inferredNextRoundToStartNumber > 0 && (
                    <div style={styles.cardMeta}>Next round to start: Round {inferredNextRoundToStartNumber}</div>
                  )}
                </>
              )}

              {!selectedRoundMatch && !selectedRoundStarted && Number(selectedRoundNumber || 0) > Number(tournament?.activeRoundNumber || 0) && (
                <div style={styles.cardMetaStrong}>
                  {selectedRoundIsQualified
                    ? `You have qualified for round ${selectedRoundNumber}. Wait for this round to start.`
                    : 'This round is not available for you yet.'}
                </div>
              )}

              {shouldShowLiveRoundCard && (
                <>
                  <div style={{ ...styles.roundHero, ...(isNarrowScreen ? styles.roundHeroNarrow : {}) }}>
                    <div style={{ ...styles.roundHeroLeft, ...(isNarrowScreen ? styles.roundHeroZoneNarrow : {}) }}>
                      <div style={styles.matchStatLabel}>Opponent</div>
                      <div style={{ ...styles.roundHeroOpponent, ...(isNarrowScreen ? styles.roundHeroOpponentNarrow : {}) }}>
                        -
                      </div>
                    </div>
                    <div style={{ ...styles.roundHeroCenter, ...(isNarrowScreen ? styles.roundHeroZoneNarrow : {}) }}>
                      <div style={styles.roundHeroMeta}>First to {tournament.matchTargetWins} wins</div>
                      <div style={styles.roundHeroScoreLine}>Score: 0 - 0</div>
                      <div style={styles.roundHeroSubMeta}>Match not played yet</div>
                    </div>
                    <div style={{ ...styles.roundHeroRight, ...(isNarrowScreen ? styles.roundHeroZoneNarrow : {}) }}>
                      <div
                        style={{
                          ...styles.resultRing,
                          ...(isNarrowScreen ? styles.resultRingNarrow : {}),
                          background: 'conic-gradient(#c97952 0deg 12deg, rgba(108, 72, 55, 0.18) 12deg 360deg)',
                        }}
                      >
                        <div style={{ ...styles.resultRingInner, ...(isNarrowScreen ? styles.resultRingInnerNarrow : {}) }}>
                          <div style={{ ...styles.resultRingState, ...(isNarrowScreen ? styles.resultRingStateNarrow : {}) }}>Pending</div>
                          <div style={styles.resultRingDivider} />
                          <div style={styles.resultRingRow}>
                            <span style={{ ...styles.resultRingLabel, ...(isNarrowScreen ? styles.resultRingLabelNarrow : {}) }}>Won</span>
                            <span style={{ ...styles.resultRingValue, ...(isNarrowScreen ? styles.resultRingValueNarrow : {}) }}>0</span>
                          </div>
                          <div style={styles.resultRingRow}>
                            <span style={{ ...styles.resultRingLabel, ...(isNarrowScreen ? styles.resultRingLabelNarrow : {}) }}>Lost</span>
                            <span style={{ ...styles.resultRingValue, ...(isNarrowScreen ? styles.resultRingValueNarrow : {}) }}>0</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={styles.cardMetaStrong}>
                    Your round is live. Send a request to start playing.
                  </div>
                </>
              )}

              {Number(selectedRoundNumber || 0) === Number(activeRoundNumber || 0) && !selectedRoundMatch && !isEliminated && (
                <>
                  <div style={styles.roundPlayerList}>
                    <div style={styles.cardMetaStrong}>Online players in Round {selectedRoundNumber}</div>
                    {eligibleOnlineRoundPlayers.length > 0 ? (
                      <div style={styles.roundPlayerRow}>
                        {eligibleOnlineRoundPlayers.map((player) => (
                          <div key={player.id} style={styles.roundPlayerChip}>
                            {player.name || player.username || 'Player'}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={styles.cardMeta}>No other round player is online yet.</div>
                    )}
                  </div>
                </>
              )}

              {/* {!selectedRoundMatch && selectedRoundStarted && Number(selectedRoundNumber || 0) === Number(tournament?.activeRoundNumber || 0) && !isEliminated && (
                <div style={styles.cardMeta}>You are waiting for your match assignment in this round.</div>
              )} */}

              {selectedRoundMatch && (
                <>
                  <div style={{ ...styles.roundHero, ...(isNarrowScreen ? styles.roundHeroNarrow : {}) }}>
                    <div style={{ ...styles.roundHeroLeft, ...(isNarrowScreen ? styles.roundHeroZoneNarrow : {}) }}>
                      <div style={styles.matchStatLabel}>Opponent</div>
                      <div style={{ ...styles.roundHeroOpponent, ...(isNarrowScreen ? styles.roundHeroOpponentNarrow : {}) }}>{selectedRoundOpponent}</div>
                    </div>
                    <div style={{ ...styles.roundHeroCenter, ...(isNarrowScreen ? styles.roundHeroZoneNarrow : {}) }}>
                      {Number(selectedRoundMatch?.winnerParticipantId ? String(selectedRoundMatch.winnerParticipantId) === myParticipantId : false) && Number(selectedRoundNumber || 0) < totalRounds && (
                        <div style={styles.roundHeroBanner}>
                          {Number(selectedRoundNumber || 0) < Number(tournament?.activeRoundNumber || 0) + 1
                            ? 'You have qualified for the next round.'
                            : 'You have qualified. Next round will open soon.'}
                        </div>
                      )}
                      <div style={styles.roundHeroMeta}>
                        {selectedRoundMatch.isBye
                          ? 'Automatic qualification for this round.'
                          : `First to ${tournament.matchTargetWins} wins`}
                      </div>
                      <div style={styles.roundHeroScoreLine}>
                        Score: {selectedRoundProgress?.scoreLine || `${selectedRoundScore.mine ?? 0} - ${selectedRoundScore.opponent ?? 0}`}
                      </div>
                      <div style={styles.roundHeroSubMeta}>
                        Played on {formatDateTime(selectedRoundMatch.completedAt || selectedRoundMatch.createdAt)}
                      </div>
                    </div>
                    <div style={{ ...styles.roundHeroRight, ...(isNarrowScreen ? styles.roundHeroZoneNarrow : {}) }}>
                      <div
                        style={{
                          ...styles.resultRing,
                          ...(isNarrowScreen ? styles.resultRingNarrow : {}),
                          background: `conic-gradient(#c97952 0deg ${Math.max(6, Math.round(((selectedRoundProgress?.minePct || 0) / 100) * 360))}deg, rgba(108, 72, 55, 0.18) ${Math.max(6, Math.round(((selectedRoundProgress?.minePct || 0) / 100) * 360))}deg 360deg)`,
                        }}
                      >
                        <div style={{ ...styles.resultRingInner, ...(isNarrowScreen ? styles.resultRingInnerNarrow : {}) }}>
                          <div style={{ ...styles.resultRingState, ...(isNarrowScreen ? styles.resultRingStateNarrow : {}) }}>{selectedRoundResult}</div>
                          <div style={styles.resultRingDivider} />
                          <div style={styles.resultRingRow}>
                            <span style={{ ...styles.resultRingLabel, ...(isNarrowScreen ? styles.resultRingLabelNarrow : {}) }}>Won</span>
                            <span style={{ ...styles.resultRingValue, ...(isNarrowScreen ? styles.resultRingValueNarrow : {}) }}>{selectedRoundProgress?.mine ?? 0}</span>
                          </div>
                          <div style={styles.resultRingRow}>
                            <span style={{ ...styles.resultRingLabel, ...(isNarrowScreen ? styles.resultRingLabelNarrow : {}) }}>Lost</span>
                            <span style={{ ...styles.resultRingValue, ...(isNarrowScreen ? styles.resultRingValueNarrow : {}) }}>{selectedRoundProgress?.opponent ?? 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {selectedRoundMatch.isBye && (
                    <div style={styles.cardMetaStrong}>You advanced by bye in this round.</div>
                  )}
                  {!selectedRoundMatch.isBye && selectedRoundScore.mine !== null && (
                    <div style={styles.cardMeta}>
                      Match result: You {selectedRoundResult.toLowerCase()} this round with a {selectedRoundProgress?.scoreLine || '0 - 0'} score against {selectedRoundOpponent || 'your opponent'}.
                    </div>
                  )}
                  {!selectedRoundResolved ? (
                    <div style={styles.cardMetaStrong}>
                      Your round is live. Send a request to start playing.
                    </div>
                  ) : Number(selectedRoundMatch?.winnerParticipantId ? String(selectedRoundMatch.winnerParticipantId) === myParticipantId : false) ? (
                    <>
                      <div style={styles.cardMetaStrong}>
                        {selectedRoundMatch.isBye
                          ? 'You qualified for the next round by bye.'
                          : (isFinalMainRound ? 'Champion! You won the final round.' : 'You won this round.')}
                      </div>
                      {!selectedRoundMatch.isBye && !isFinalMainRound && (
                        <div style={styles.cardMeta}>
                          Great job. You are still in the tournament race and your next round will open as soon as it is ready.
                        </div>
                      )}
                      {nextQualifiedRoundNumber > 0 && nextQualifiedRoundNumber <= totalRounds && !isFinalMainRound && (
                        <div style={styles.cardMeta}>
                          Your name has automatically moved to Round {nextQualifiedRoundNumber}. Wait for that round to start.
                        </div>
                      )}
                    </>
                  ) : selectedRoundLost ? (
                    <>
                      <div style={styles.cardMetaStrong}>
                        {shouldShowPlacementInstruction
                          ? `You have to play another matches to be the prize winner for rank ${placementInstructionRange}.`
                          : hasResolvedPlacementMatch
                            ? 'Your extra prize match has already been completed.'
                            : 'Oops, you have lost in this round. Better luck for next time.'}
                      </div>
                      {selectedPlacementContext && (
                        <div style={styles.cardMeta}>
                          Your main round is over, but you can still become a prize winner through the placement matches for rank {placementInstructionRange}.
                        </div>
                      )}
                    </>
                  ) : null}
                </>
              )}

              {canPlayCurrentRound && (
                <>
                  {!isSubMatchRound && !selectedRoundResolved && (
                    <div style={styles.cardMeta}>
                      Any unplayed player in round {effectivePlayableRound?.roundNumber || selectedRoundNumber || 1} can accept your request.
                    </div>
                  )}
                  {isSubMatchRound && (
                    <div style={styles.cardMetaStrong}>
                      {effectivePlayableRound?.label || 'Sub Match'} is live. Send a request to play your extra prize match.
                    </div>
                  )}
                  {!isSubMatchRound && isEliminated && (
                    <div style={styles.cardMeta}>
                      Your main bracket run is over, but your current playable match is still available here.
                    </div>
                  )}
                <button
                  type="button"
                  onClick={handleSendTournamentRequest}
                  disabled={sendingRequest || shouldShowRoundNotStartedButton}
                  style={{ ...styles.requestBtn, ...((sendingRequest || shouldShowRoundNotStartedButton) ? styles.requestBtnDisabled : {}) }}
                >
                  {sendingRequest
                    ? 'Sending Request...'
                    : shouldShowRoundNotStartedButton
                      ? 'Round Not Started Yet'
                      : isSubMatchRound
                        ? `Send ${effectivePlayableRound?.label || 'Sub Match'} Request`
                        : 'Send Round Request'}
                </button>
                </>
              )}
            </div>

            {mainRoundHistory.length > 0 && (
              <div style={styles.card}>
                <div style={styles.cardTitle}>Main Round History</div>
                <div style={styles.cardMeta}>Your played rounds, opponents, and results.</div>
                <div style={styles.subMatchHistory}>
                  {mainRoundHistory.map((item) => (
                    <div key={item.id} style={styles.subMatchHistoryRow}>
                      <div>
                        <div style={styles.cardMetaStrong}>{item.label}</div>
                        <div style={styles.cardMeta}>Opponent: {item.opponent}</div>
                      </div>
                      <div style={styles.subMatchHistoryMeta}>
                        <span style={styles.subMatchHistoryResult}>{item.result}</span>
                        <span>{item.scoreLine}</span>
                        <span>{item.playedAt}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(placementHistory.length > 0 || (isSubMatchRound && effectivePlayableRound)) && (
              <div style={styles.card}>
                <div style={styles.cardTitle}>Sub Match</div>
                <div style={styles.cardMeta}>
                  {effectivePlayableRound?.label || latestPlacementMatch?.roundLabel || 'Extra prize match'}
                </div>
                {latestPlacementMatch ? (
                  <>
                    <div style={{ ...styles.roundHero, ...(isNarrowScreen ? styles.roundHeroNarrow : {}) }}>
                      <div style={{ ...styles.roundHeroLeft, ...(isNarrowScreen ? styles.roundHeroZoneNarrow : {}) }}>
                        <div style={styles.matchStatLabel}>Opponent</div>
                        <div style={{ ...styles.roundHeroOpponent, ...(isNarrowScreen ? styles.roundHeroOpponentNarrow : {}) }}>{latestPlacementOpponent}</div>
                      </div>
                      <div style={{ ...styles.roundHeroCenter, ...(isNarrowScreen ? styles.roundHeroZoneNarrow : {}) }}>
                        <div style={styles.roundHeroMeta}>First to {tournament.matchTargetWins} wins</div>
                        <div style={styles.roundHeroScoreLine}>
                          Score: {latestPlacementProgress?.scoreLine || '0 - 0'}
                        </div>
                        <div style={styles.roundHeroSubMeta}>
                          Played on {formatDateTime(latestPlacementMatch.completedAt || latestPlacementMatch.createdAt)}
                        </div>
                      </div>
                      <div style={{ ...styles.roundHeroRight, ...(isNarrowScreen ? styles.roundHeroZoneNarrow : {}) }}>
                        <div
                          style={{
                            ...styles.resultRing,
                            ...(isNarrowScreen ? styles.resultRingNarrow : {}),
                            background: `conic-gradient(#c97952 0deg ${Math.max(6, Math.round(((latestPlacementProgress?.minePct || 0) / 100) * 360))}deg, rgba(108, 72, 55, 0.18) ${Math.max(6, Math.round(((latestPlacementProgress?.minePct || 0) / 100) * 360))}deg 360deg)`,
                          }}
                        >
                          <div style={{ ...styles.resultRingInner, ...(isNarrowScreen ? styles.resultRingInnerNarrow : {}) }}>
                            <div style={{ ...styles.resultRingState, ...(isNarrowScreen ? styles.resultRingStateNarrow : {}) }}>{latestPlacementResult}</div>
                            <div style={styles.resultRingDivider} />
                            <div style={styles.resultRingRow}>
                              <span style={{ ...styles.resultRingLabel, ...(isNarrowScreen ? styles.resultRingLabelNarrow : {}) }}>Won</span>
                              <span style={{ ...styles.resultRingValue, ...(isNarrowScreen ? styles.resultRingValueNarrow : {}) }}>{latestPlacementProgress?.mine ?? 0}</span>
                            </div>
                            <div style={styles.resultRingRow}>
                              <span style={{ ...styles.resultRingLabel, ...(isNarrowScreen ? styles.resultRingLabelNarrow : {}) }}>Lost</span>
                              <span style={{ ...styles.resultRingValue, ...(isNarrowScreen ? styles.resultRingValueNarrow : {}) }}>{latestPlacementProgress?.opponent ?? 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={styles.cardMetaStrong}>
                    Your extra prize match is active. Use the request button to start it.
                  </div>
                )}
                {placementHistory.length > 0 && (
                  <div style={styles.subMatchHistory}>
                    <div style={styles.cardMetaStrong}>Sub Match History</div>
                    {placementHistory.map((item) => (
                      <div key={item.id} style={styles.subMatchHistoryRow}>
                        <div>
                          <div style={styles.primaryText}>{item.label}</div>
                          <div style={styles.secondaryText}>vs {item.opponent}</div>
                        </div>
                        <div style={styles.subMatchHistoryMeta}>
                          <span style={styles.subMatchHistoryResult}>{item.result}</span>
                          <span>{item.scoreLine}</span>
                          <span>{item.playedAt}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        )}
      </div>
      {incomingTournamentReq && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <div style={styles.cardTitle}>Tournament Request</div>
            <div style={styles.cardMeta}>
              <b>{incomingTournamentReq.from?.name}</b> wants to play <b>{incomingTournamentReq.roundLabel || `round ${incomingTournamentReq.roundNumber}`}</b> in <b>{incomingTournamentReq.tournamentName}</b>.
            </div>
            <div style={styles.modalActions}>
              <button type="button" onClick={() => handleTournamentRequestResponse(true)} style={{ ...styles.modalActionBtn, ...styles.modalAcceptBtn }}>Accept</button>
              <button type="button" onClick={() => handleTournamentRequestResponse(false)} style={{ ...styles.modalActionBtn, ...styles.modalRejectBtn }}>Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    padding: '10px 12px 16px',
    background:
      'radial-gradient(circle at 18% 0%, rgba(243,197,111,0.22), transparent 44%), radial-gradient(circle at 92% 12%, rgba(120,170,255,0.16), transparent 42%), radial-gradient(circle at 65% 52%, rgba(255,214,165,0.08), transparent 52%), linear-gradient(180deg, #1f120c 0%, #1a0f0a 52%, #0e0705 100%)',
    overflowX: 'hidden',
    boxSizing: 'border-box',
  },
  shell: {
    width: '100%',
    maxWidth: 1280,
    margin: '0 auto',
    display: 'grid',
    gap: 10,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  kicker: { color: '#f3c56f', textTransform: 'uppercase', letterSpacing: 1.3, fontSize: 12, fontWeight: 900 },
  title: { color: '#ffffff', fontSize: 22, fontWeight: 900, marginTop: 2, lineHeight: 1.1 },
  meta: { color: 'rgba(255,255,255,0.74)', marginTop: 2, fontSize: 13, lineHeight: 1.35 },
  backBtn: { border: '1px solid rgba(255,214,155,0.18)', background: 'rgba(10,6,4,0.35)', color: '#fff4df', borderRadius: 12, padding: '8px 14px', fontWeight: 900, cursor: 'pointer' },
  stack: { display: 'grid', gap: 10 },
  summaryBar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '4px 14px',
    padding: 0,
  },
  summaryItem: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 6,
    color: '#f6e7d4',
  },
  summaryKey: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  summaryText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  timelineCard: {
    width: '100%',
    background: 'linear-gradient(180deg, rgba(40,24,16,0.96), rgba(24,14,10,0.98))',
    borderRadius: 18,
    padding: '12px 14px',
    border: '1px solid rgba(255,214,155,0.16)',
    display: 'grid',
    gap: 10,
    overflowX: 'auto',
    boxSizing: 'border-box',
  },
  timelineWrap: { display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0, width: '100%', minWidth: '100%', paddingBottom: 2, overflowX: 'auto', scrollBehavior: 'smooth', WebkitOverflowScrolling: 'touch' },
  roundDot: { border: '1px solid rgba(255,214,155,0.14)', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.84)', borderRadius: 14, width: 78, minWidth: 78, height: 58, display: 'grid', placeItems: 'center', gap: 1, cursor: 'pointer', padding: '6px 4px', boxSizing: 'border-box' },
  roundDotDone: { background: 'rgba(255,255,255,0.07)', color: 'rgba(255,231,189,0.92)', borderColor: 'rgba(255,214,155,0.18)' },
  roundDotCurrent: { background: 'linear-gradient(145deg, rgba(122,196,141,0.88), rgba(53,93,39,0.88))', color: '#fffaf7', boxShadow: '0 0 0 3px rgba(122,196,141,0.20)' },
  roundDotSelected: { border: '1px solid rgba(255,214,155,0.34)', transform: 'translateY(-1px)' },
  roundDotLocked: { opacity: 0.5, cursor: 'not-allowed' },
  roundDotNumber: { fontSize: 15, fontWeight: 900, lineHeight: 1 },
  roundDotLabel: { fontSize: 10, fontWeight: 800 },
  timelineLine: { flex: 1, minWidth: 12, height: 2, background: 'rgba(255,214,155,0.20)' },
  card: { background: 'linear-gradient(180deg, rgba(40,24,16,0.96), rgba(24,14,10,0.98))', borderRadius: 18, padding: '12px 14px', border: '1px solid rgba(255,214,155,0.16)', boxSizing: 'border-box', color: 'rgba(255,255,255,0.86)' },
  cardTitle: { color: '#ffffff', fontSize: 17, fontWeight: 900, lineHeight: 1.2 },
  cardMeta: { color: 'rgba(255,255,255,0.70)', fontSize: 13, marginTop: 6, lineHeight: 1.4 },
  cardMetaStrong: { color: '#ffffff', fontSize: 13, marginTop: 6, lineHeight: 1.4, fontWeight: 900 },
  roundHero: {
    marginTop: 14,
    padding: 14,
    borderRadius: 16,
    border: '1px solid rgba(255,214,155,0.16)',
    background: 'radial-gradient(circle at 18% 0%, rgba(243,197,111,0.14), transparent 44%), linear-gradient(180deg, rgba(40,24,16,0.96), rgba(24,14,10,0.98))',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.15fr) minmax(0, 1fr)',
    gap: 12,
    alignItems: 'center',
  },
  roundHeroNarrow: {
    gridTemplateColumns: '1fr',
    gap: 10,
    padding: 12,
  },
  roundHeroLeft: {
    minWidth: 0,
    display: 'grid',
    gap: 4,
    alignContent: 'center',
    justifyItems: 'start',
  },
  roundHeroZoneNarrow: {
    justifyItems: 'center',
    textAlign: 'center',
  },
  roundHeroCenter: {
    minWidth: 0,
    display: 'grid',
    justifyItems: 'center',
    textAlign: 'center',
    gap: 6,
    alignContent: 'center',
    paddingInline: 8,
  },
  roundHeroBanner: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 34,
    padding: '7px 14px',
    borderRadius: 999,
    background: 'linear-gradient(145deg, rgba(122,196,141,0.20), rgba(53,93,39,0.14))',
    border: '1px solid rgba(122,196,141,0.22)',
    color: '#d7ffdc',
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.2,
  },
  roundHeroOpponent: {
    color: '#ffffff',
    fontSize: 24,
    fontWeight: 900,
    lineHeight: 1.1,
  },
  roundHeroOpponentNarrow: {
    fontSize: 18,
    wordBreak: 'break-word',
  },
  roundHeroMeta: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: 13,
    lineHeight: 1.4,
  },
  roundHeroSubMeta: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: 12,
    lineHeight: 1.35,
  },
  roundHeroScoreLine: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: 800,
  },
  roundHeroRight: {
    display: 'grid',
    placeItems: 'center',
    justifySelf: 'stretch',
  },
  resultRing: {
    width: 112,
    height: 112,
    borderRadius: '50%',
    padding: 6,
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    justifySelf: 'center',
  },
  resultRingNarrow: {
    width: 92,
    height: 92,
    padding: 5,
  },
  resultRingInner: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    background: 'radial-gradient(circle at top, rgba(255,255,255,0.10) 0%, rgba(0,0,0,0.22) 100%)',
    border: '1px solid rgba(255,214,155,0.16)',
    display: 'grid',
    justifyItems: 'center',
    alignContent: 'center',
    gap: 3,
    textAlign: 'center',
    padding: 10,
  },
  resultRingInnerNarrow: {
    padding: 8,
    gap: 2,
  },
  resultRingState: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
    lineHeight: 1,
  },
  resultRingStateNarrow: {
    fontSize: 11,
  },
  resultRingDivider: {
    width: 24,
    height: 1,
    background: 'rgba(255,214,155,0.24)',
    margin: '1px 0',
  },
  resultRingRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 4,
    lineHeight: 1,
  },
  resultRingLabel: {
    color: 'rgba(255,255,255,0.64)',
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  resultRingLabelNarrow: {
    fontSize: 9,
  },
  resultRingValue: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: 900,
  },
  resultRingValueNarrow: {
    fontSize: 13,
  },
  roundPlayerList: {
    marginTop: 14,
    paddingTop: 12,
    borderTop: '1px solid rgba(255,214,155,0.12)',
    display: 'grid',
    gap: 8,
  },
  roundPlayerRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  roundPlayerChip: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    padding: '8px 12px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,214,155,0.14)',
    color: 'rgba(255,255,255,0.86)',
    fontSize: 12,
    fontWeight: 800,
  },
  subMatchHistory: {
    marginTop: 14,
    paddingTop: 12,
    borderTop: '1px solid rgba(255,214,155,0.12)',
    display: 'grid',
    gap: 10,
  },
  subMatchHistoryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    padding: '10px 12px',
    borderRadius: 12,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,214,155,0.12)',
  },
  subMatchHistoryMeta: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    alignItems: 'center',
    color: 'rgba(255,255,255,0.70)',
    fontSize: 12,
    fontWeight: 700,
  },
  subMatchHistoryResult: {
    color: '#ffffff',
    fontWeight: 800,
    textTransform: 'uppercase',
  },
  matchStatLabel: { color: 'rgba(255,255,255,0.62)', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8 },
  notice: { background: 'rgba(0,0,0,0.24)', borderRadius: 14, padding: '12px 14px', color: 'rgba(255,255,255,0.80)', border: '1px solid rgba(255,214,155,0.14)', fontWeight: 800 },
  status: { display: 'inline-flex', alignItems: 'center', borderRadius: 999, padding: '6px 10px', marginTop: 8, fontWeight: 800, textTransform: 'capitalize' },
  requestBtn: { marginTop: 14, border: '1px solid rgba(122,196,141,0.22)', background: 'linear-gradient(145deg, rgba(88,168,106,0.92), rgba(46,112,64,0.92))', color: '#fffaf7', borderRadius: 12, padding: '10px 14px', cursor: 'pointer', fontWeight: 900, fontSize: 13, boxShadow: '0 10px 18px rgba(0,0,0,0.18)' },
  requestBtnDisabled: { opacity: 0.55, cursor: 'not-allowed' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)', display: 'grid', placeItems: 'center', padding: 20, zIndex: 3000 },
  modalCard: { width: 'min(520px, 100%)', background: 'linear-gradient(180deg, rgba(40,24,16,0.98), rgba(24,14,10,0.99))', borderRadius: 18, padding: 18, border: '1px solid rgba(255,214,155,0.16)', display: 'grid', gap: 12, boxShadow: '0 26px 70px rgba(0,0,0,0.45)' },
  modalActions: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'stretch' },
  modalActionBtn: { minWidth: 120, height: 48, borderRadius: 14, padding: '0 18px', cursor: 'pointer', fontWeight: 800, fontSize: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' },
  modalAcceptBtn: { border: '1px solid rgba(122,196,141,0.22)', background: 'linear-gradient(145deg, rgba(88,168,106,0.92), rgba(46,112,64,0.92))', color: '#fffaf7' },
  modalRejectBtn: { border: '1px solid rgba(255,214,155,0.18)', background: 'rgba(10,6,4,0.35)', color: 'rgba(255,255,255,0.86)' },
};
